import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import Urls from '../../../_core/constants/urls';
import { HEADERS, RecurringContributionItem } from './models';
import { RecurringService } from './recurring.service';
import { debounceTime, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { PaymentService } from '../../../_core/api/payment.service';
import { PaymentStatus } from '../../../_core/constants/Payment';
import { ModalsService } from '../../../_core/services/modals.service';
import { Modals } from '../../../_core/constants/modals';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';

import { GlobalConfigService } from '../../../_core/services/global-config.service';

@Component({
  selector: 'app-manage-recurring',
  templateUrl: './manage-recurring.component.html',
  styleUrls: ['./manage-recurring.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageRecurringComponent implements OnInit, OnDestroy {
  urls = Urls;
  headers = Object.values(HEADERS);
  pageSize = 10;
  paymentStatus = PaymentStatus;
  defaultDateFormat = 'yyyy-MM-dd';
  dateFormat: string;

  destroy$ = new Subject<void>();
  tableLoading$ = new BehaviorSubject<boolean>(true);
  pageLoading$ = new BehaviorSubject<boolean>(false);
  pageNumber$ = new BehaviorSubject<number>(1);
  totalPages$ = new BehaviorSubject<number>(1);
  totalItems$ = new BehaviorSubject<number>(0);
  tableData$ = new BehaviorSubject<RecurringContributionItem[]>([]);

  firstIndex$ = this.pageNumber$.pipe(map((pageNumber) => (pageNumber - 1) * this.pageSize + 1));
  lastIndex$ = this.firstIndex$.pipe(
    map((firstIndex) => Math.min(firstIndex + this.pageSize - 1, this.totalItems$.value))
  );

  constructor(
    private router: Router,
    private recurringService: RecurringService,
    private paymentService: PaymentService,
    private modalService: ModalsService,
    private globalConfigApiService: GlobalConfigApiService,
    private globalConfigService: GlobalConfigService
  ) {}

  ngOnInit(): void {
    this.getDateFormat();
    this.requestTableData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDateFormat(): void {
    const globalConfig = this.globalConfigService.getGlobalConfig();
    if (globalConfig?.isLoaded) {
      this.dateFormat = globalConfig.dateFormat;
    } else {
      this.globalConfigApiService
        .getConfiguration()
        .pipe(map(({ response }) => response.dateFormat))
        .subscribe((dateFormat) => (this.dateFormat = dateFormat || this.defaultDateFormat));
    }
  }

  requestTableData(): void {
    this.pageNumber$
      .pipe(
        tap(() => this.pageLoading$.next(true)),
        debounceTime(400),
        switchMap((pageNumber) => this.recurringService.getRecurringSchedules(pageNumber)),
        takeUntil(this.destroy$)
      )
      .subscribe(({ data, totalPages, totalItems }) => {
        data = data.sort((a, b) => b.createdDate - a.createdDate); // Sort by createdDate descending
        this.totalPages$.next(totalPages);
        this.tableLoading$.next(false);
        this.pageLoading$.next(false);
        this.tableData$.next(data);
        this.totalItems$.next(totalItems);
      });
  }

  goTo(url: string, queryParams = {}): void {
    this.router.navigate([`${Urls.APP}/${url}`], { queryParams });
  }

  getRowItems(row: any): string[] {
    return Object.values(row);
  }

  cancelSubscription(subscriptionId: string): void {
    this.modalService.openModal(Modals.CANCEL_SUBSCRIPTION, {
      displayName: 'DONATE_MANAGE_RECURRING_CONTRIBUTION_MODAL_TITLE',
      infoMessage: 'DONATE_MANAGE_RECURRING_CONTRIBUTION_MODAL_INFO',
    });

    this.modalService.modalResponse$
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => {
          this.pageLoading$.next(true);
          return this.paymentService
            .cancelSubscription(subscriptionId)
            .pipe(tap(() => this.pageNumber$.next(this.pageNumber$.value)));
        })
      )
      .subscribe();
  }

  onPageChanged({ page }: PageChangedEvent): void {
    this.pageNumber$.next(page);
  }
}
